import { useEffect, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { fetchDisponibilites, saveDisponibilites } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Save, Loader2, MessageCircle } from "lucide-react";

const toDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toIso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export default function DispoCalendar({ annonceId, isOwner = false, whatsappNumber = "", annonceTitle = "", priceLabel = "" }) {
  const [blocked, setBlocked] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [wanted, setWanted] = useState([]);

  useEffect(() => {
    fetchDisponibilites(annonceId)
      .then((d) => { setBlocked(d.blocked_dates || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [annonceId]);

  if (!loaded) return null;

  const blockedDates = blocked.map(toDate);
  const canBook = !isOwner && whatsappNumber;

  const openWhatsApp = () => {
    const dates = wanted.slice().sort().join(", ");
    const msg = `Bonjour ! Je souhaite reserver "${annonceTitle}" pour les dates suivantes : ${dates}.${priceLabel ? ` (${priceLabel})` : ""} Sont-elles disponibles ? Merci ! (via fodindin.com)`;
    const num = whatsappNumber.replace(/\D/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank", "noreferrer");
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await saveDisponibilites(annonceId, blocked);
      setDirty(false);
      toast.success("Disponibilités enregistrées ✓");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <section className="glass-card p-6 mt-6" data-testid="dispo-calendar">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h2 className="font-bold text-lg inline-flex items-center gap-2">
          <CalendarDays size={18} className="text-orange-500" /> Disponibilités
        </h2>
        {isOwner && dirty && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn-primary !py-1.5 !px-4 text-sm inline-flex items-center gap-1.5"
            data-testid="dispo-save-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--ink-soft)] mb-3">
        {isOwner
          ? "Cliquez sur les dates pour les bloquer/débloquer (rouge = occupé), puis Enregistrer."
          : "Les dates barrées en rouge sont déjà réservées. Contactez le propriétaire pour les dates libres."}
      </p>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <Calendar
          mode="multiple"
          selected={blockedDates}
          onSelect={isOwner ? (dates) => { setBlocked((dates || []).map(toIso)); setDirty(true); } : undefined}
          disabled={isOwner ? { before: new Date() } : blockedDates}
          modifiers={{ blocked: blockedDates }}
          modifiersClassNames={{
            blocked: "!bg-red-100 !text-red-600 line-through !rounded-md font-bold",
          }}
          className="rounded-2xl border border-[var(--line)] bg-white"
        />
        <div className="text-xs space-y-2" data-testid="dispo-legend">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-red-100 border border-red-300 inline-block" />
            <span className="line-through text-red-600 font-semibold">Occupé / bloqué</span>
            <span className="text-[var(--ink-soft)]">({blocked.length} date{blocked.length > 1 ? "s" : ""})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-white border border-[var(--line)] inline-block" />
            <span className="font-semibold text-emerald-700">Libre</span>
          </div>
          {canBook && wanted.length > 0 && (
            <button
              type="button"
              onClick={openWhatsApp}
              className="mt-2 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-full transition-colors"
              data-testid="dispo-whatsapp-btn"
            >
              <MessageCircle size={15} /> Réserver ces {wanted.length} date{wanted.length > 1 ? "s" : ""} via WhatsApp
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
